import { Behavior } from '../Behavior';
import { TaskStatus } from '../TaskStatus';
import { Blackboard } from '../Blackboard';
/**
 * 设置黑板变量值
 *
 * @description 将指定值或另一个黑板变量的值设置到目标变量
 *
 * @example
 * ```typescript
 * // 设置固定值
 * const setHealth = new SetBlackboardValue<GameContext>('playerHealth', 100);
 *
 * // 从另一个变量复制值
 * const copyValue = new SetBlackboardValue<GameContext>('targetHealth', null, 'playerHealth');
 * ```
 */
export class SetBlackboardValue extends Behavior {
    constructor(variableName, value = null, sourceVariable, force = false) {
        super();
        this.variableName = variableName;
        this.value = value;
        this.sourceVariable = sourceVariable;
        this.force = force;
    }
    update(context) {
        const blackboard = context.blackboard;
        if (!blackboard || !(blackboard instanceof Blackboard)) {
            console.warn('SetBlackboardValue: 上下文中未找到Blackboard实例');
            return TaskStatus.Failure;
        }
        let valueToSet;
        if (this.sourceVariable) {
            if (!blackboard.hasVariable(this.sourceVariable)) {
                console.warn(`SetBlackboardValue: 源变量 "${this.sourceVariable}" 不存在`);
                return TaskStatus.Failure;
            }
            valueToSet = blackboard.getValue(this.sourceVariable);
        }
        else {
            valueToSet = this.value;
        }
        const success = blackboard.setValue(this.variableName, valueToSet, this.force);
        return success ? TaskStatus.Success : TaskStatus.Failure;
    }
}
/**
 * 增加数值型黑板变量
 *
 * @description 将数值型变量增加指定的数值，支持从另一个变量获取增量
 */
export class AddToBlackboardValue extends Behavior {
    constructor(variableName, increment, incrementVariable) {
        super();
        this.variableName = variableName;
        this.increment = increment;
        this.incrementVariable = incrementVariable;
    }
    update(context) {
        const blackboard = context.blackboard;
        if (!blackboard || !(blackboard instanceof Blackboard)) {
            console.warn('AddToBlackboardValue: 上下文中未找到Blackboard实例');
            return TaskStatus.Failure;
        }
        if (!blackboard.hasVariable(this.variableName)) {
            console.warn(`AddToBlackboardValue: 变量 "${this.variableName}" 不存在`);
            return TaskStatus.Failure;
        }
        const currentValue = blackboard.getValue(this.variableName);
        if (typeof currentValue !== 'number') {
            console.warn(`AddToBlackboardValue: 变量 "${this.variableName}" 不是数值类型`);
            return TaskStatus.Failure;
        }
        let incrementValue;
        if (this.incrementVariable) {
            if (!blackboard.hasVariable(this.incrementVariable)) {
                console.warn(`AddToBlackboardValue: 增量变量 "${this.incrementVariable}" 不存在`);
                return TaskStatus.Failure;
            }
            incrementValue = blackboard.getValue(this.incrementVariable);
            if (typeof incrementValue !== 'number') {
                console.warn(`AddToBlackboardValue: 增量变量 "${this.incrementVariable}" 不是数值类型`);
                return TaskStatus.Failure;
            }
        }
        else {
            incrementValue = this.increment;
        }
        const newValue = currentValue + incrementValue;
        const success = blackboard.setValue(this.variableName, newValue);
        return success ? TaskStatus.Success : TaskStatus.Failure;
    }
}
/**
 * 切换布尔型黑板变量
 *
 * @description 将布尔型变量的值取反
 */
export class ToggleBlackboardBool extends Behavior {
    constructor(variableName) {
        super();
        this.variableName = variableName;
    }
    update(context) {
        const blackboard = context.blackboard;
        if (!blackboard || !(blackboard instanceof Blackboard)) {
            console.warn('ToggleBlackboardBool: 上下文中未找到Blackboard实例');
            return TaskStatus.Failure;
        }
        if (!blackboard.hasVariable(this.variableName)) {
            console.warn(`ToggleBlackboardBool: 变量 "${this.variableName}" 不存在`);
            return TaskStatus.Failure;
        }
        const currentValue = blackboard.getValue(this.variableName);
        if (typeof currentValue !== 'boolean') {
            console.warn(`ToggleBlackboardBool: 变量 "${this.variableName}" 不是布尔类型`);
            return TaskStatus.Failure;
        }
        const success = blackboard.setValue(this.variableName, !currentValue);
        return success ? TaskStatus.Success : TaskStatus.Failure;
    }
}
/**
 * 重置黑板变量到默认值
 *
 * @description 将指定变量重置为其定义时的默认值
 */
export class ResetBlackboardVariable extends Behavior {
    constructor(variableName) {
        super();
        this.variableName = variableName;
    }
    update(context) {
        const blackboard = context.blackboard;
        if (!blackboard || !(blackboard instanceof Blackboard)) {
            console.warn('ResetBlackboardVariable: 上下文中未找到Blackboard实例');
            return TaskStatus.Failure;
        }
        const success = blackboard.resetVariable(this.variableName);
        return success ? TaskStatus.Success : TaskStatus.Failure;
    }
}
/**
 * 等待黑板变量满足条件
 *
 * @description 等待指定的黑板变量满足某个条件，常用于同步操作
 */
export class WaitForBlackboardCondition extends Behavior {
    constructor(variableName, expectedValue, compareFn) {
        super();
        this.variableName = variableName;
        this.expectedValue = expectedValue;
        this.compareFn = compareFn;
    }
    update(context) {
        const blackboard = context.blackboard;
        if (!blackboard || !(blackboard instanceof Blackboard)) {
            console.warn('WaitForBlackboardCondition: 上下文中未找到Blackboard实例');
            return TaskStatus.Failure;
        }
        if (!blackboard.hasVariable(this.variableName)) {
            console.warn(`WaitForBlackboardCondition: 变量 "${this.variableName}" 不存在`);
            return TaskStatus.Failure;
        }
        const currentValue = blackboard.getValue(this.variableName);
        let conditionMet;
        if (this.compareFn) {
            conditionMet = this.compareFn(currentValue, this.expectedValue);
        }
        else {
            conditionMet = currentValue === this.expectedValue;
        }
        return conditionMet ? TaskStatus.Success : TaskStatus.Running;
    }
}
/**
 * 记录黑板变量到控制台
 *
 * @description 将黑板变量的当前值记录到控制台，用于调试
 */
export class LogBlackboardValue extends Behavior {
    constructor(variableName, prefix = '[Blackboard]') {
        super();
        this.variableName = variableName;
        this.prefix = prefix;
    }
    update(context) {
        const blackboard = context.blackboard;
        if (!blackboard || !(blackboard instanceof Blackboard)) {
            console.warn('LogBlackboardValue: 上下文中未找到Blackboard实例');
            return TaskStatus.Failure;
        }
        if (!blackboard.hasVariable(this.variableName)) {
            console.warn(`LogBlackboardValue: 变量 "${this.variableName}" 不存在`);
            return TaskStatus.Failure;
        }
        const value = blackboard.getValue(this.variableName);
        const variableDefinition = blackboard.getVariableDefinition(this.variableName);
        console.log(`${this.prefix} ${this.variableName} (${variableDefinition?.type}):`, value);
        return TaskStatus.Success;
    }
}
/**
 * 数学运算黑板变量
 *
 * @description 对数值型黑板变量执行数学运算
 */
export var MathOperation;
(function (MathOperation) {
    MathOperation["Add"] = "add";
    MathOperation["Subtract"] = "subtract";
    MathOperation["Multiply"] = "multiply";
    MathOperation["Divide"] = "divide";
    MathOperation["Modulo"] = "modulo";
    MathOperation["Power"] = "power";
    MathOperation["Min"] = "min";
    MathOperation["Max"] = "max";
})(MathOperation || (MathOperation = {}));
export class MathBlackboardOperation extends Behavior {
    constructor(targetVariable, operand1Variable, operand2, operation) {
        super();
        this.targetVariable = targetVariable;
        this.operand1Variable = operand1Variable;
        this.operand2 = operand2;
        this.operation = operation;
    }
    update(context) {
        const blackboard = context.blackboard;
        if (!blackboard || !(blackboard instanceof Blackboard)) {
            console.warn('MathBlackboardOperation: 上下文中未找到Blackboard实例');
            return TaskStatus.Failure;
        }
        // 获取第一个操作数
        if (!blackboard.hasVariable(this.operand1Variable)) {
            console.warn(`MathBlackboardOperation: 操作数变量 "${this.operand1Variable}" 不存在`);
            return TaskStatus.Failure;
        }
        const operand1 = blackboard.getValue(this.operand1Variable);
        if (typeof operand1 !== 'number') {
            console.warn(`MathBlackboardOperation: 操作数变量 "${this.operand1Variable}" 不是数值类型`);
            return TaskStatus.Failure;
        }
        // 获取第二个操作数
        let operand2;
        if (typeof this.operand2 === 'string') {
            if (!blackboard.hasVariable(this.operand2)) {
                console.warn(`MathBlackboardOperation: 操作数变量 "${this.operand2}" 不存在`);
                return TaskStatus.Failure;
            }
            operand2 = blackboard.getValue(this.operand2);
            if (typeof operand2 !== 'number') {
                console.warn(`MathBlackboardOperation: 操作数变量 "${this.operand2}" 不是数值类型`);
                return TaskStatus.Failure;
            }
        }
        else {
            operand2 = this.operand2;
        }
        // 执行数学运算
        let result;
        try {
            switch (this.operation) {
                case MathOperation.Add:
                    result = operand1 + operand2;
                    break;
                case MathOperation.Subtract:
                    result = operand1 - operand2;
                    break;
                case MathOperation.Multiply:
                    result = operand1 * operand2;
                    break;
                case MathOperation.Divide:
                    if (operand2 === 0) {
                        console.warn('MathBlackboardOperation: 除数不能为零');
                        return TaskStatus.Failure;
                    }
                    result = operand1 / operand2;
                    break;
                case MathOperation.Modulo:
                    if (operand2 === 0) {
                        console.warn('MathBlackboardOperation: 模运算的除数不能为零');
                        return TaskStatus.Failure;
                    }
                    result = operand1 % operand2;
                    break;
                case MathOperation.Power:
                    result = Math.pow(operand1, operand2);
                    break;
                case MathOperation.Min:
                    result = Math.min(operand1, operand2);
                    break;
                case MathOperation.Max:
                    result = Math.max(operand1, operand2);
                    break;
                default:
                    console.warn(`MathBlackboardOperation: 不支持的数学操作 "${this.operation}"`);
                    return TaskStatus.Failure;
            }
        }
        catch (error) {
            console.error('MathBlackboardOperation: 数学运算执行失败:', error);
            return TaskStatus.Failure;
        }
        // 设置结果
        const success = blackboard.setValue(this.targetVariable, result);
        return success ? TaskStatus.Success : TaskStatus.Failure;
    }
}
//# sourceMappingURL=BlackboardActions.js.map